// ────────────────────────────────────────────────────────────────────────────
// The Document Builder layout contract.
//
// A template is `{ page, theme, bands[] }`. Bands are an *ordered, toggleable*
// stack of structured blocks (header → organisation → bill-to → lines → totals →
// text/bank → footer) rather than free-positioned boxes. That choice is
// deliberate: a band stack cannot produce a document that breaks on page 2,
// and it still covers the branding customers actually ask for (their logo,
// their colours, their column set, their payment terms). Free positioning,
// if we ever want it, slots in later as a new band kind — not as a rewrite.
//
// Everything here is pure data and must stay JSON-serialisable: this object
// is what will land in `OrganisationDocumentTemplate.LayoutJson` when the
// backend arrives. `schemaVersion` is stamped on every template so a future
// shape change can be migrated on read instead of breaking old rows.
// ────────────────────────────────────────────────────────────────────────────

import type { LabelOverrides } from './labels';

/** Document kinds the builder can design. Invoice + credit note to start. */
/**
 * The document kinds an organisation designs layouts for.
 *
 * Three rather than one because the legally required CONTENT differs, not just
 * the wording: a credit note must not demand payment and must reference the
 * invoice it corrects, and a pro forma must be marked as not being a VAT
 * invoice and must not carry an invoice number. The shared envelope
 * (letterhead, bill-to, line table, totals) is identical, so each kind's stock
 * layout is the same design with a different band set - see `stockTemplates`.
 */
export const DOC_TYPES = ['Invoice', 'CreditNote', 'Proforma'] as const;
export type DocumentType = (typeof DOC_TYPES)[number];

export const DOC_TYPE_LABEL: Record<DocumentType, string> = {
  Invoice: 'Invoice',
  CreditNote: 'Credit note',
  Proforma: 'Pro forma',
};

/**
 * Current layout shape. Bump when a band option set changes incompatibly, and
 * teach `migrateLayout` how to bring older saved layouts forward.
 *
 * v2 — the `seller` band and `{{seller.*}}` tokens became `organisation`.
 * v3 — the line table's `details` and `description` columns merged into one
 *      `description` column carrying `detailsMode`.
 * v4 — the `header` band split into `logo` + `title`, and bands gained
 *      `column` for the header grid.
 * v5 — the `billTo` band split into `customer` + `docDetails`, so each can take
 *      its own column instead of sharing one band's internal split.
 */
export const LAYOUT_SCHEMA_VERSION = 5;

export type Align = 'left' | 'center' | 'right';

/** react-pdf ships these three families without a font registration step. */
export const FONT_FAMILIES = ['Helvetica', 'Times-Roman', 'Courier'] as const;
export type FontFamily = (typeof FONT_FAMILIES)[number];

export const PAGE_SIZES = ['A4', 'LETTER'] as const;
export type PageSize = (typeof PAGE_SIZES)[number];

export const PAGE_ORIENTATIONS = ['portrait', 'landscape'] as const;
export type PageOrientation = (typeof PAGE_ORIENTATIONS)[number];

export const PAGE_ORIENTATION_LABEL: Record<PageOrientation, string> = {
  portrait: 'Portrait',
  landscape: 'Landscape',
};

/**
 * Columns a band can occupy. Three is the practical ceiling on A4 — a fourth
 * leaves each column too narrow for an address.
 */
export const BAND_COLUMNS = [1, 2, 3] as const;
export type BandColumn = (typeof BAND_COLUMNS)[number];

export interface PageSettings {
  size: PageSize;
  /** Uniform page margin in points (36pt ≈ 12.7mm). */
  marginPt: number;
  /**
   * Portrait unless set. Landscape exists for the transport column set: date,
   * order, route, reference, trailer, container and cargo alongside four money
   * columns simply does not fit 523pt of A4 portrait, and shrinking them to fit
   * makes the text overlap rather than wrap.
   */
  orientation?: PageOrientation;
}

export interface ThemeSettings {
  fontFamily: FontFamily;
  /** Body text size in points; every other size scales off this by default. */
  baseFontSizePt: number;

  /**
   * Optional pins for the rest of the scale. Unset means "follow the body size"
   * via the offsets in `lib/typeScale.ts`, which is how every template behaved
   * before these existed — so leaving them alone changes nothing.
   */
  smallFontSizePt?: number;
  organisationNameSizePt?: number;
  totalFontSizePt?: number;
  lineFontSizePt?: number;
  /** Body copy. */
  textColor: string;
  /** Labels, secondary lines. */
  mutedColor: string;
  /** Document title + table-head text — the brand colour, usually. */
  accentColor: string;
  /** Table header row fill. */
  tableHeadColor: string;
  /** Rules and table borders. */
  borderColor: string;
}

// ── Field registries ───────────────────────────────────────────────────────
// Each band that shows "a set of fields" stores an *ordered* array of keys.
// Order in the array = order on the page, so the inspector can reorder as
// well as toggle. The label registries below drive the inspector checkboxes;
// the renderer switches on the same keys.

export const ORGANISATION_FIELDS = [
  'name',
  'addressLines',
  'taxNumber',
  'registrationNumber',
  'phone',
  'email',
  'website',
] as const;
export type OrganisationField = (typeof ORGANISATION_FIELDS)[number];

export const ORGANISATION_FIELD_LABEL: Record<OrganisationField, string> = {
  name: 'Organisation name',
  addressLines: 'Address',
  taxNumber: 'VAT number',
  registrationNumber: 'Company reg. number',
  phone: 'Phone',
  email: 'Email',
  website: 'Website',
};

export const PARTY_FIELDS = [
  'name',
  'code',
  'addressLines',
  'taxNumber',
  'contactName',
  'phone',
  'email',
] as const;
export type PartyField = (typeof PARTY_FIELDS)[number];

export const PARTY_FIELD_LABEL: Record<PartyField, string> = {
  name: 'Customer name',
  code: 'Customer code',
  addressLines: 'Billing address',
  taxNumber: 'Customer VAT number',
  contactName: 'Contact name',
  phone: 'Phone',
  email: 'Email',
};

/**
 * How a stored one-line address is broken across printed lines. Addresses have
 * no structured line 1 / line 2 in this system — see `lib/addressFormat.ts`.
 */
export const ADDRESS_FORMATS = ['oneLine', 'perComma', 'wrapped'] as const;
export type AddressFormat = (typeof ADDRESS_FORMATS)[number];

export const ADDRESS_FORMAT_LABEL: Record<AddressFormat, string> = {
  oneLine: 'One line (wraps)',
  perComma: 'A line per comma',
  wrapped: 'Wrapped to a width',
};

export const META_FIELDS = [
  'issueDate',
  'dueDate',
  'poNumber',
  'reference',
  'currency',
  'paymentTerms',
] as const;
export type MetaField = (typeof META_FIELDS)[number];

export const META_FIELD_LABEL: Record<MetaField, string> = {
  issueDate: 'Issue date',
  dueDate: 'Due date',
  poNumber: 'PO number',
  reference: 'Reference',
  currency: 'Currency',
  paymentTerms: 'Payment terms',
};

/** How a line's date is formatted. Patterns, not locale names, so what the
 *  operator picks is exactly what prints. */
export const LINE_DATE_FORMATS = ['dd/MM/yyyy', 'dd/MM/yy', 'dd MMM yyyy', 'yyyy-MM-dd'] as const;
export type LineDateFormat = (typeof LINE_DATE_FORMATS)[number];

/** An order has several tasks, so "the date" has to say which one. */
export const LINE_DATE_SOURCES = ['collection', 'delivery'] as const;
export type LineDateSource = (typeof LINE_DATE_SOURCES)[number];

export const LINE_DATE_SOURCE_LABEL: Record<LineDateSource, string> = {
  collection: 'Collection date',
  delivery: 'Delivery date',
};

/**
 * What the Description column prints for a line that carries route context.
 * `description` keeps the charge text even when a route is available, which is
 * how the column behaved before it absorbed the old Details column.
 */
export const LINE_DETAILS_MODES = ['fromTo', 'collection', 'delivery', 'description'] as const;
export type LineDetailsMode = (typeof LINE_DETAILS_MODES)[number];

export const LINE_DETAILS_MODE_LABEL: Record<LineDetailsMode, string> = {
  fromTo: 'Route: From → To',
  collection: 'Collection place',
  delivery: 'Delivery place',
  description: 'Charge description',
};

export const LINE_CARGO_MODES = ['product', 'temperature', 'both'] as const;
export type LineCargoMode = (typeof LINE_CARGO_MODES)[number];

// "Goods", not "Product": this is the order's free-text cargo summary, and
// "Product" now means the organisation's own products (see `productsDisplay`).
export const LINE_CARGO_MODE_LABEL: Record<LineCargoMode, string> = {
  product: 'Goods',
  temperature: 'Temperature',
  both: 'Goods + temperature',
};

/**
 * Where the products a line carries print (Settings → Products in use): their
 * own column, a second line under the description, or not at all.
 */
export const PRODUCTS_DISPLAYS = ['hidden', 'column', 'belowDescription'] as const;
export type ProductsDisplay = (typeof PRODUCTS_DISPLAYS)[number];

export const PRODUCTS_DISPLAY_LABEL: Record<ProductsDisplay, string> = {
  hidden: "Don't show",
  column: 'Own column',
  belowDescription: 'Under the description',
};

export const LINE_COLUMNS = [
  'date',
  'orderNumber',
  'description',
  'orderRef',
  'trailer',
  'container',
  'cargo',
  'products',
  'reference',
  'quantity',
  'unitPrice',
  'net',
  'taxRatePct',
  'taxAmount',
  'gross',
] as const;
export type LineColumnKey = (typeof LINE_COLUMNS)[number];

export const LINE_COLUMN_LABEL: Record<LineColumnKey, string> = {
  date: 'Date',
  orderRef: 'Order reference',
  trailer: 'Trailer',
  container: 'Container',
  cargo: 'Cargo',
  products: 'Products',
  description: 'Description',
  orderNumber: 'Order number',
  reference: 'Line reference',
  quantity: 'Qty',
  unitPrice: 'Unit price',
  net: 'Net',
  taxRatePct: 'VAT %',
  taxAmount: 'VAT',
  gross: 'Total',
};

/**
 * A column in the lines table. `description` is the flex column that soaks up
 * leftover width; every other column is fixed at `widthPt`. `label` is
 * user-editable so an org can print "Goods" instead of "Description".
 */
export interface LineColumn {
  key: LineColumnKey;
  label: string;
  visible: boolean;
  widthPt: number;
  align: Align;

  // ── Per-column options. All optional: a column that never needed one, and
  //    layouts saved before these existed, fall back to the defaults in
  //    `lib/lineColumns.ts`.

  /** `date`: which of the order's dates to print. */
  dateSource?: LineDateSource;
  /** `date`: the pattern to print it in. */
  dateFormat?: LineDateFormat;
  /** `description`: what to show on a line that carries route context. */
  detailsMode?: LineDetailsMode;
  /** `orderRef`: which of the order's reference slots (1-based). */
  referenceSlot?: number;
  /** `cargo`: product, temperature, or both. */
  cargoMode?: LineCargoMode;
}

export const TOTAL_ROWS = ['subtotal', 'vatTotal', 'total'] as const;
export type TotalRowKey = (typeof TOTAL_ROWS)[number];

export const TOTAL_ROW_LABEL: Record<TotalRowKey, string> = {
  subtotal: 'Subtotal (ex VAT)',
  vatTotal: 'VAT',
  total: 'Total',
};

export const BANK_FIELDS = [
  'accountName',
  'bankName',
  'iban',
  'bic',
  'sortCode',
  'accountNumber',
] as const;
export type BankField = (typeof BANK_FIELDS)[number];

export const BANK_FIELD_LABEL: Record<BankField, string> = {
  accountName: 'Account name',
  bankName: 'Bank',
  iban: 'IBAN',
  bic: 'BIC / SWIFT',
  sortCode: 'Sort code',
  accountNumber: 'Account number',
};

// ── Bands ──────────────────────────────────────────────────────────────────

export const BAND_KINDS = [
  'logo',
  'title',
  'organisation',
  'customer',
  'docDetails',
  'lines',
  'totals',
  'text',
  'bank',
  'footer',
] as const;
export type BandKind = (typeof BAND_KINDS)[number];

export const BAND_KIND_LABEL: Record<BandKind, string> = {
  logo: 'Logo',
  title: 'Document title',
  organisation: 'Organisation details',
  customer: 'Bill to',
  docDetails: 'Document details',
  lines: 'Line items',
  totals: 'Totals',
  text: 'Text block',
  bank: 'Payment details',
  footer: 'Page footer',
};

interface BandBase {
  /** Stable id — the reorder / select / delete key. */
  id: string;
  kind: BandKind;
  /** Unticked bands stay in the layout but do not print. */
  enabled: boolean;
  /** Vertical gap in points printed after this band. */
  spaceAfterPt: number;
  /**
   * Which column of the **header grid** this band sits in, 1-based. Only means
   * anything for bands above the line table — see `DocumentLayout.headerColumns`.
   * Unset behaves as column 1.
   */
  column?: BandColumn;
}

/**
 * The organisation's logo, on its own so it can sit in a different column from
 * the document title — the arrangement most invoices actually use.
 */
export interface LogoBand extends BandBase {
  kind: 'logo';
  align: Align;
  widthPt: number;
}

export interface TitleBand extends BandBase {
  kind: 'title';
  /** Supports tokens, so `{{doc.typeName}}` follows the document kind. */
  text: string;
  align: Align;
  sizePt: number;
  uppercase: boolean;
  showNumber: boolean;
  numberLabel: string;
  /** Hairline rule underneath. */
  showRule: boolean;
}

/**
 * Per-field text that replaces what the organisation record would print.
 * A missing key (or a blank value) means "use the record" — see
 * `lib/organisationFields.ts` for the resolution rule. Optional so layouts
 * saved before overrides existed load unchanged.
 */
export type OrganisationFieldOverrides = Partial<Record<OrganisationField, string>>;

export interface OrganisationBand extends BandBase {
  kind: 'organisation';
  align: Align;
  fields: OrganisationField[];
  overrides?: OrganisationFieldOverrides;
}

/**
 * The customer the document is addressed to.
 *
 * Split from the document details it used to share a band with, so the two can
 * take different columns of the header grid. Put them in the same column and
 * they stack; put them in different columns and you get the classic
 * customer-left / dates-right masthead — which is what the old band's internal
 * two-column switch did, now expressed the same way as every other placement.
 */
export interface CustomerBand extends BandBase {
  kind: 'customer';
  heading: string;
  fields: PartyField[];
  /**
   * How to break the customer's stored one-line address. Optional; the renderer
   * defaults to `oneLine`, matching what the invoice PDF has always printed.
   */
  addressFormat?: AddressFormat;
  /** Character budget per line when `addressFormat` is `wrapped`. */
  addressWrapChars?: number;
}

/** Issue date, due date, PO number and friends — label/value rows. */
export interface DocDetailsBand extends BandBase {
  kind: 'docDetails';
  heading: string;
  fields: MetaField[];
  /**
   * Renamed labels — "PO number" as "Your order ref". Only the labels: the
   * values come from the document, and a per-template value would print one
   * document's details on every one.
   */
  labels?: LabelOverrides<MetaField>;
  /** Right-align the rows — usual when the block sits in a right-hand column. */
  align: 'left' | 'right';
}

export interface LinesBand extends BandBase {
  kind: 'lines';
  columns: LineColumn[];
  showHeader: boolean;
  /** Alternating row fill — easier to read on 20+ line invoices. */
  zebra: boolean;
  /** Print an `Order NNN` subheading above each order group. */
  groupByOrder: boolean;
  /**
   * Leave the order-context cells (date, order number, reference, trailer,
   * container, cargo) blank on a line that continues the same order — so a fuel
   * surcharge or waiting-time charge sits under its order without restating it.
   * Defaults to true; see `CONTEXT_COLUMNS` in `lib/lineColumns.ts`.
   */
  blankRepeatedContext?: boolean;
  /**
   * Where the products a line carries print. Optional: layouts saved before
   * products existed — and every organisation not using them — read as
   * `hidden`, so no existing document changes.
   */
  productsDisplay?: ProductsDisplay;
}

export interface TotalsBand extends BandBase {
  kind: 'totals';
  rows: TotalRowKey[];
  /**
   * Renamed row labels — "Subtotal (ex VAT)" as "Goods total", "VAT" as "Sales
   * Tax". The VAT label also prefixes the per-rate breakdown lines, so the two
   * cannot drift apart. Optional: layouts saved before this load unchanged.
   */
  rowLabels?: LabelOverrides<TotalRowKey>;
  /** One line per VAT rate above the totals — required in some jurisdictions. */
  showVatBreakdown: boolean;
  align: 'left' | 'right';
  widthPt: number;
}

export interface TextBand extends BandBase {
  kind: 'text';
  heading: string;
  /** Free copy with `{{token}}` interpolation. Newlines are honoured. */
  body: string;
  align: Align;
  /** Draw a light box around the block. */
  boxed: boolean;
}

export interface BankBand extends BandBase {
  kind: 'bank';
  heading: string;
  fields: BankField[];
  boxed: boolean;
}

export interface FooterBand extends BandBase {
  kind: 'footer';
  /** Tokens allowed. Printed at the bottom of *every* page. */
  body: string;
  align: Align;
  showPageNumbers: boolean;
  showRule: boolean;
}

export type Band =
  | LogoBand
  | TitleBand
  | OrganisationBand
  | CustomerBand
  | DocDetailsBand
  | LinesBand
  | TotalsBand
  | TextBand
  | BankBand
  | FooterBand;

export interface DocumentLayout {
  page: PageSettings;
  theme: ThemeSettings;
  /** Print order, top to bottom. The footer band is page-fixed regardless. */
  bands: Band[];
  /**
   * How many columns the **header grid** has — the whole region above the line
   * table, laid out as one grid rather than a stack of full-width rows.
   *
   * Treating that region as a single grid is what lets the organisation block
   * sit directly under the document number while the customer sits under the
   * logo. As separate rows it cannot: a row is as tall as its tallest band, so a
   * logo pushes everything after it down the page.
   *
   * Unset means "as many columns as the bands actually use", so a layout whose
   * logo is in column 1 and title in column 3 keeps its three-column masthead
   * without anyone having to declare it.
   */
  headerColumns?: BandColumn;

  /**
   * Relative widths of the header columns, e.g. `[3, 2]` for a 60/40 split.
   * Omitted or too short means equal.
   */
  columnWeights?: number[];

  /** Gap above the header grid, in points. */
  headerGapBeforePt?: number;
  /**
   * Gap below the header grid. Unset falls back to the largest gap its own bands
   * ask for, which is what the region spaced itself by before it was a grid.
   */
  headerGapAfterPt?: number;
}

/**
 * A saved design. Mirrors the planned `OrganisationDocumentTemplate` row:
 * `id`/`name`/`docType`/`isDefault`/`schemaVersion`/`layout` (as LayoutJson).
 * `isStock` is builder-only — it marks the read-only templates we ship so the
 * UI can offer "Duplicate" instead of "Edit".
 */
export interface DocumentTemplate {
  id: string;
  docType: DocumentType;
  name: string;
  isDefault: boolean;
  isStock: boolean;
  schemaVersion: number;
  updatedUtc: string;
  layout: DocumentLayout;
}
